import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout';
import { useI18n } from '../../contexts/I18nContext';

export default function Licenses() {
  const { legal, isRTL } = useI18n();
  const licenses = legal.legal.privacy.licenses;
  const sections = licenses.sections;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F5FF] to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-4xl">
          {/* Back Link */}
          <div className="pt-16 mb-8">
            <Link
              to="/legal"
              className={`inline-flex items-center text-[#5B3FD0] hover:text-[#4A2FB0] transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <svg className={`w-5 h-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {legal.legal.pages.licenses.title}
            </Link>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border-t-4 border-[#5B3FD0]">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">{licenses.title}</h1>
            <p className="text-[#707070] mb-2">{licenses.lastUpdated}</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#707070] mt-4">
              <span>{licenses.appInfo.appName.label} <strong>{licenses.appInfo.appName.value}</strong></span>
              <span>{licenses.appInfo.developer.label} <strong>{licenses.appInfo.developer.value}</strong></span>
              <span>{licenses.appInfo.contact.label} <strong>{licenses.appInfo.contact.value}</strong></span>
              <span>{licenses.appInfo.website.label} <a href={licenses.appInfo.website.value} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">{licenses.appInfo.website.value}</a></span>
            </div>
          </div>

          {/* Introduction */}
          <Section title={sections.introduction.title}>
            {sections.introduction.content.map((p: string, i: number) => (
              <p key={i} className="text-[#555] leading-relaxed mb-3">{p}</p>
            ))}
          </Section>

          {/* Open Source Libraries */}
          <Section title={sections.openSource.title}>
            <p className="text-[#555] mb-4">{sections.openSource.description}</p>
            <div className="grid gap-3">
              {sections.openSource.libraries.map((lib: any, i: number) => (
                <div key={i} className="flex items-center justify-between bg-[#F8F5FF] rounded-lg p-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 flex-wrap">
                      <a href={lib.link} target="_blank" rel="noopener noreferrer" className="font-bold text-[#5B3FD0] hover:underline text-lg">
                        {lib.name}
                      </a>
                      <span className="inline-flex px-2 py-1 bg-white rounded text-xs font-medium text-[#707070] border">
                        {lib.license}
                      </span>
                    </div>
                    <p className="text-sm text-[#555] mt-1">{lib.purpose}</p>
                  </div>
                  <a href={lib.link} target="_blank" rel="noopener noreferrer" className="text-[#5B3FD0] hover:text-[#4A2FB0] ml-3 flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#707070] mt-4 italic">{sections.openSource.note}</p>
          </Section>

          {/* Firebase */}
          <Section title={sections.firebase.title}>
            <p className="text-[#555] mb-4">{sections.firebase.description}</p>
            <ul className="space-y-2 list-disc list-inside text-[#555] mb-4">
              {sections.firebase.services.map((service: string, i: number) => (
                <li key={i} className="leading-relaxed">{service}</li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-4">
              {sections.firebase.links.map((link: any, i: number) => (
                <a
                  key={i}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 bg-[#F8F5FF] text-[#5B3FD0] rounded-lg hover:bg-[#EDE8FF] transition-colors text-sm font-medium"
                >
                  {link.label}
                  <svg className={`w-4 h-4 ${isRTL ? 'mr-1' : 'ml-1'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              ))}
            </div>
          </Section>

          {/* Fonts */}
          <Section title={sections.fonts.title}>
            <p className="text-[#555] mb-4">{sections.fonts.description}</p>
            <div className="space-y-4">
              {sections.fonts.fonts.map((font: any, i: number) => (
                <div key={i} className="bg-[#F8F5FF] rounded-lg p-5">
                  <div className="flex items-center gap-3 flex-wrap mb-2">
                    <a href={font.link} target="_blank" rel="noopener noreferrer" className="font-bold text-[#5B3FD0] hover:underline text-lg">
                      {font.name}
                    </a>
                    <span className="inline-flex px-2 py-1 bg-white rounded text-xs font-medium text-[#707070] border">
                      {font.license}
                    </span>
                  </div>
                  <p className="text-[#555] text-sm">{font.usage} — {font.creator}</p>
                  {font.linkText && (
                    <a href={font.link} target="_blank" rel="noopener noreferrer" className="text-sm text-[#5B3FD0] hover:underline mt-1 inline-block">
                      {font.linkText}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* Images and Icons */}
          <Section title={sections.imagesAndIcons.title}>
            {/* Unsplash */}
            <SubSection title={sections.imagesAndIcons.unsplash.title}>
              <p className="text-[#555] leading-relaxed mb-2">{sections.imagesAndIcons.unsplash.description}</p>
              <p className="text-[#555] text-sm">
                {sections.imagesAndIcons.unsplash.licenseLabel} <strong>{sections.imagesAndIcons.unsplash.licenseName}</strong>
              </p>
            </SubSection>

            {/* Heroicons */}
            <SubSection title={sections.imagesAndIcons.heroicons.title}>
              <p className="text-[#555] leading-relaxed mb-2">{sections.imagesAndIcons.heroicons.description}</p>
              <div className="bg-[#F8F5FF] rounded-lg p-4 space-y-1 text-sm text-[#555]">
                <p>{sections.imagesAndIcons.heroicons.creatorLabel} <strong>{sections.imagesAndIcons.heroicons.creator}</strong></p>
                <p>{sections.imagesAndIcons.heroicons.licenseLabel} <strong>{sections.imagesAndIcons.heroicons.license}</strong></p>
              </div>
            </SubSection>

            {/* Custom Graphics */}
            <SubSection title={sections.imagesAndIcons.customGraphics.title}>
              <p className="text-[#555] leading-relaxed">{sections.imagesAndIcons.customGraphics.description}</p>
            </SubSection>
          </Section>

          {/* AI Services */}
          <Section title={sections.aiServices.title}>
            <p className="text-[#555] leading-relaxed mb-3">{sections.aiServices.description}</p>
            <div className="bg-[#F8F5FF] rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-[#2C2C2C] mb-2">{sections.aiServices.services.title}</h4>
              <ul className="space-y-1 list-disc list-inside text-[#555]">
                {sections.aiServices.services.items.map((item: string, i: number) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
            <p className="text-sm text-[#707070] italic">{sections.aiServices.disclaimer}</p>
          </Section>

          {/* Backend Infrastructure */}
          <Section title={sections.backendInfrastructure.title}>
            <p className="text-[#555] mb-4">{sections.backendInfrastructure.description}</p>
            <div className="grid gap-3">
              {sections.backendInfrastructure.technologies.map((tech: any, i: number) => (
                <div key={i} className="flex items-start gap-3 bg-[#F8F5FF] rounded-lg p-4">
                  <div className="w-2 h-2 rounded-full bg-[#5B3FD0] mt-2 flex-shrink-0" />
                  <div>
                    <strong className="text-[#2C2C2C]">{tech.name}</strong>
                    <span className="text-[#555]"> — {tech.description}</span>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          {/* Data Privacy */}
          <Section title={sections.dataPrivacy.title}>
            <p className="text-[#555] leading-relaxed mb-3">
              {sections.dataPrivacy.description}{' '}
              <Link to="/legal/privacy" className="text-[#5B3FD0] hover:underline font-medium">
                {sections.dataPrivacy.policyLinkText}
              </Link>
            </p>
            <div className="bg-[#F8F5FF] rounded-lg p-4">
              <h4 className="font-semibold text-[#2C2C2C] mb-2">{sections.dataPrivacy.security.title}</h4>
              <ul className="space-y-1 list-disc list-inside text-[#555]">
                {sections.dataPrivacy.security.measures.map((m: string, i: number) => (
                  <li key={i} className="leading-relaxed">{m}</li>
                ))}
              </ul>
            </div>
          </Section>

          {/* User Content Ownership */}
          <Section title={sections.userContentOwnership.title}>
            <p className="text-[#555] leading-relaxed mb-4">{sections.userContentOwnership.description}</p>
            <div className="bg-[#F8F5FF] rounded-lg p-4">
              <h4 className="font-semibold text-[#2C2C2C] mb-2">{sections.userContentOwnership.rights.title}</h4>
              <ul className="space-y-1 list-disc list-inside text-[#555]">
                {sections.userContentOwnership.rights.items.map((item: string, i: number) => (
                  <li key={i} className="leading-relaxed">{item}</li>
                ))}
              </ul>
            </div>
          </Section>

          {/* MIT License */}
          <Section title={sections.mitLicense.title}>
            <p className="text-[#555] mb-4">{sections.mitLicense.description}</p>
            <pre className="bg-gray-900 text-gray-100 rounded-lg p-6 text-sm overflow-x-auto whitespace-pre-wrap leading-relaxed font-mono">
              {sections.mitLicense.licenseText}
            </pre>
          </Section>

          {/* Trademarks */}
          <Section title={sections.trademarks.title}>
            <p className="text-[#555] mb-4">{sections.trademarks.description}</p>
            <ul className="space-y-2 list-disc list-inside text-[#555]">
              {sections.trademarks.trademarks.map((item: string, i: number) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
            <p className="text-sm text-[#707070] mt-4 italic">{sections.trademarks.disclaimer}</p>
          </Section>

          {/* Copyright */}
          <Section title={sections.copyright.title}>
            {sections.copyright.notices.map((notice: string, i: number) => (
              <p key={i} className="text-[#555] leading-relaxed mb-2">{notice}</p>
            ))}
          </Section>

          {/* Licensing Contact */}
          <Section title={sections.licensingContact.title}>
            <p className="text-[#555] mb-4">{sections.licensingContact.description}</p>
            <div className="bg-[#F8F5FF] rounded-lg p-6 space-y-2">
              <p className="text-[#555]">
                {sections.licensingContact.email.label}{' '}
                <a href={`mailto:${sections.licensingContact.email.value}`} className="text-[#5B3FD0] hover:underline">
                  {sections.licensingContact.email.value}
                </a>
              </p>
              <p className="text-[#555]">
                {sections.licensingContact.website.label}{' '}
                <a href={sections.licensingContact.website.value} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">
                  {sections.licensingContact.website.value}
                </a>
              </p>
            </div>
          </Section>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-sm text-[#707070]">
              © {new Date().getFullYear()} {legal.legal.mainPage.footer.rights} <strong>{legal.legal.mainPage.company}</strong>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
      <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4 pb-2 border-b-2 border-[#5B3FD0]">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-lg font-semibold text-[#2C2C2C] mb-3">{title}</h3>
      {children}
    </div>
  );
}
